import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Globe, Mail, Phone, ArrowLeft, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  return String(detail);
}

export default function ForgotPasswordPage() {
  const { t, lang, toggleLang } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  if (token) return <ResetForm token={token} />;
  return <RequestForm />;
}

function RequestForm() {
  const { t, lang, toggleLang } = useLanguage();
  const [mode, setMode] = useState('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = mode === 'email' ? { email } : { phone };
      const res = await axios.post(`${API}/auth/forgot-password`, payload);
      setSuccess(true);
      if (res.data.token) setResetToken(res.data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4 py-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <Link to="/login" className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A]">
            <ArrowLeft className="w-4 h-4" /> {lang === 'ar' ? 'العودة' : 'Back to Login'}
          </Link>
          <button onClick={toggleLang} className="flex items-center gap-1 text-sm text-[#475569]">
            <Globe className="w-4 h-4" /> {lang === 'en' ? 'العربية' : 'English'}
          </button>
        </div>

        <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading mb-2">
          {lang === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
        </h1>
        <p className="text-sm text-[#475569] mb-6">
          {lang === 'ar' ? 'أدخل بياناتك لاستعادة حسابك' : 'Enter your details to recover your account'}
        </p>

        {success ? (
          <div className="text-center py-8" data-testid="reset-success">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-[#0F172A] font-medium mb-2">
              {lang === 'ar' ? 'تم إرسال رابط إعادة التعيين' : 'Reset link generated'}
            </p>
            <p className="text-sm text-[#475569] mb-4">
              {lang === 'ar' ? 'استخدم الرابط أدناه لإعادة تعيين كلمة المرور' : 'Use the link below to reset your password'}
            </p>
            {resetToken && (
              <Link
                to={`/reset-password?token=${resetToken}`}
                className="inline-block px-4 py-2 bg-[#B48E4B] text-white rounded-lg text-sm hover:bg-[#8C6A32] transition-colors"
                data-testid="reset-link"
              >
                {lang === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password Now'}
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="flex bg-slate-100 rounded-lg p-1 mb-6">
              <button type="button" onClick={() => setMode('email')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'email' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#475569]'}`}>
                <Mail className="w-4 h-4" /> {t('email')}
              </button>
              <button type="button" onClick={() => setMode('phone')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'phone' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#475569]'}`}>
                <Phone className="w-4 h-4" /> {t('phone')}
              </button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'email' ? (
                <div>
                  <Label className="text-sm">{t('email')}</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required={mode === 'email'} className="mt-1 h-11" placeholder="name@example.com" data-testid="forgot-email-input" />
                </div>
              ) : (
                <div>
                  <Label className="text-sm">{t('phone')}</Label>
                  <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)} required={mode === 'phone'} className="mt-1 h-11" placeholder="+962 7X XXX XXXX" dir="ltr" data-testid="forgot-phone-input" />
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0F172A] text-white hover:bg-[#1E293B] text-sm" data-testid="forgot-submit">
                {loading ? '...' : (lang === 'ar' ? 'إرسال رابط الإعادة' : 'Send Reset Link')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function ResetForm({ token }) {
  const { lang, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError(lang === 'ar' ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4 py-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-6">
          <button onClick={toggleLang} className="flex items-center gap-1 text-sm text-[#475569]">
            <Globe className="w-4 h-4" /> {lang === 'en' ? 'العربية' : 'English'}
          </button>
        </div>

        <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading mb-2">
          {lang === 'ar' ? 'كلمة مرور جديدة' : 'Set New Password'}
        </h1>

        {success ? (
          <div className="text-center py-8" data-testid="reset-complete">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-[#0F172A] font-medium mb-4">
              {lang === 'ar' ? 'تم تغيير كلمة المرور بنجاح' : 'Password reset successfully!'}
            </p>
            <Button onClick={() => navigate('/login')} className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="goto-login-after-reset">
              {lang === 'ar' ? 'تسجيل الدخول' : 'Go to Login'}
            </Button>
          </div>
        ) : (
          <>
            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-sm">{lang === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}</Label>
                <div className="relative mt-1">
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required className="h-11 pe-10" data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute top-1/2 -translate-y-1/2 end-3 text-[#94A3B8]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0F172A] text-white hover:bg-[#1E293B] text-sm" data-testid="reset-password-submit">
                {loading ? '...' : (lang === 'ar' ? 'تعيين كلمة المرور' : 'Reset Password')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
